/**
 * firmwareShort — a compact firmware label for dense tables (the Devices list).
 *
 * Why: the raw `firmware` string owgw reports is long and vendor-specific, e.g.
 *   "OpenWrt 23.05-SNAPSHOT r23485+100-e92cf0c46f / TIP-v4.2.7-02acc6"
 *   "sonicfi_rsw210g-2pv_v5.0.0-rc5_release_202606051780651053"
 *   "Edgecore 12.5.93 ddd37fa7e / EC-12.5.93-1467"
 * A list column only needs the release that matters for triage ("TIP 4.2.7", "Shasta",
 * "EC 12.5.93", "v5.0.0-rc5"); the full string stays available as a tooltip.
 *
 * Rules are ordered most-specific first. Anything unrecognised falls back to the part after
 * " / " (the NOS half of "OpenWrt … / <nos>") or the raw string, trimmed — never an empty label.
 * Note: the controller stores firmware in a 64-char column, so long Shasta strings arrive
 * truncated ("… / Shasta-AP-NOS Rel"); the Shasta rule tolerates that.
 */

const MAX_FALLBACK = 24;

export function firmwareShort(raw: string | null | undefined): string {
  const fw = (raw ?? "").trim();
  if (!fw) return "—";

  // TIP builds: "TIP-v4.2.7-02acc6", "TIP-v5.1.0 v5.1.0-rc2-…", "TIP-devel-3bed98c".
  const tip = fw.match(/TIP-v(\d+(?:\.\d+)+)/);
  if (tip) return `TIP ${tip[1]}`;
  if (/TIP-devel/.test(fw)) return "TIP devel";

  // Shasta (vendor NOS on TIP base); the release suffix is usually cut off by the 64-char column.
  if (/Shasta/i.test(fw)) return "Shasta";

  // Edgecore stock firmware: "… / EC-12.5.93-1467".
  const ec = fw.match(/\bEC-(\d+(?:\.\d+)+)/);
  if (ec) return `EC ${ec[1]}`;

  // SonicFi/CyberTan switches: "sonicfi_rsw210g-2pv_v5.0.0-rc5_release_2026…".
  const sw = fw.match(/_v(\d[\w.-]*?)_release/);
  if (sw) return `v${sw[1]}`;

  // Already short (e.g. ECS switch "v4.1.2_rc4"): show as-is.
  if (fw.length <= MAX_FALLBACK) return fw;

  // Generic "<base> / <nos>": keep the NOS half, then trim.
  const nos = fw.includes(" / ") ? fw.split(" / ").pop()!.trim() : fw;
  return nos.length <= MAX_FALLBACK ? nos : `${nos.slice(0, MAX_FALLBACK - 1)}…`;
}
